import React, { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { Dialog } from "components/ui/Dialog";
import { Select } from "components/ui/Select/Select";
import { onInvalidSubmit } from "utils/reportFormErrors";

import {
  getReviewTypeTitle,
  isCdm,
  isOcf,
  PROGRAMME_LABELS,
} from "../../constants/reviewTypes";
import { getDeniedActionsByGroup } from "../../utils/allowedActions";
import style from "./PCRSAddProgrammeDialog.module.css";

const formSchema = z.object({
  reviewType: z.string().min(1, { message: "Please select a review type." }),
});

// Full programme titles shown only in this selection dialog — elsewhere the
// shared REVIEW_TYPE_TITLES abbreviations (OCF/PP/CDM) are kept.
const REVIEW_TYPE_FULL_TITLES = {
  OCF_INITIAL: "Opportunistic Case Finding Initial",
  OCF_SUBSEQUENT: "Opportunistic Case Finding Subsequent",
  PP_REGISTRATION: "Prevention Programme Registration",
  PP_ANNUAL: "Prevention Programme Annual Review",
  CDM_REGISTRATION: "Chronic Disease Management Registration",
  CDM_INTERIM: "Chronic Disease Management Interim Review",
  CDM_ANNUAL: "Chronic Disease Management Annual Review",
};

export const PCRSAddProgrammeDialog = ({
  isPcrsAddProgrammeDialogOpen,
  setIsPcrsAddProgrammeDialogOpen,
  onAddOcfButtonClick,
  onAddPpButtonClick,
  onAddCdmButtonClick,
  allowedActions,
  deniedActions,
  preselectedReviewType = "",
}) => {
  const options = (allowedActions ?? []).map((action) => ({
    label:
      REVIEW_TYPE_FULL_TITLES[action.reviewType] ??
      getReviewTypeTitle(action.reviewType),
    value: action.reviewType,
  }));

  // Show each denied programme with its own reason (per-programme, never a global
  // block) so a user understands why only some programmes are selectable.
  const deniedGroups = getDeniedActionsByGroup(deniedActions);
  const deniedProgrammes = ["OCF", "PP", "CDM"]
    .map((programme) => ({
      programme,
      reason: deniedGroups[programme]?.[0]?.reason ?? null,
    }))
    .filter((entry) => !!entry.reason);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: { reviewType: "" },
  });

  // Opened from a Programme availability card: start on that programme. Only
  // preselect a type the patient is actually allowed, so a stale value can never
  // put the Select on an option that is not in the list.
  const isPreselectable = options.some(
    (option) => option.value === preselectedReviewType
  );

  useEffect(() => {
    if (!isPcrsAddProgrammeDialogOpen) return;

    form.reset({ reviewType: isPreselectable ? preselectedReviewType : "" });
  }, [isPcrsAddProgrammeDialogOpen, preselectedReviewType, isPreselectable, form]);

  const handleFormSubmit = (data) => {
    setIsPcrsAddProgrammeDialogOpen(false);
    form.reset();

    if (isOcf(data.reviewType)) {
      onAddOcfButtonClick(data.reviewType);
    } else if (isCdm(data.reviewType)) {
      onAddCdmButtonClick(data.reviewType);
    } else {
      onAddPpButtonClick(data.reviewType);
    }
  };

  return (
    <Dialog
      open={isPcrsAddProgrammeDialogOpen}
      title="Select Programme"
      onClose={() => {
        setIsPcrsAddProgrammeDialogOpen(false);
        form.reset();
      }}
      width={540}
    >
      <FormProvider {...form}>
        <form
          className={style.addReferralForm}
          onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("PCRSAddProgrammeDialog"))}
        >
          <FormField name="reviewType">
            <label className={style.addReferralFormLabel}>Review Type</label>
            <Select
              fullWidth={true}
              options={options}
              placeholder="Select review type"
              value={form.watch("reviewType") ?? ""}
              onChange={(value) =>
                form.setValue("reviewType", value, { shouldValidate: true })
              }
              required
            />
          </FormField>

          {deniedProgrammes.length > 0 && (
            <div className={style.deniedSection}>
              <p className={style.deniedTitle}>Not available for this patient</p>
              {deniedProgrammes.map(({ programme, reason }) => (
                <div
                  key={programme}
                  className={style.deniedRow}
                  title={reason}
                >
                  <span className={style.deniedProgramme}>
                    {PROGRAMME_LABELS[programme] ?? programme}
                  </span>
                  <span className={style.deniedReason}>{reason}</span>
                </div>
              ))}
            </div>
          )}

          <div className={style.bottomSection}>
            <button
              type="button"
              className={style.cancelButton}
              onClick={() => {
                setIsPcrsAddProgrammeDialogOpen(false);
                form.reset();
              }}
            >
              Cancel
            </button>
            <button type="submit" className={style.sendButton}>
              Continue
            </button>
          </div>
        </form>
      </FormProvider>
    </Dialog>
  );
};
